import { useState } from 'react';
import { Switch, Button, Drawer, Typography, Space, Divider, FloatButton, Tooltip } from '@duxton-web/ui';
import { FlagFilled, CloseUniversal } from '@duxton-web/icons';
import {
  clearAllGrabXOverrides,
  clearGrabXCache,
  clearGrabXOverride,
  getGrabXOverrides,
  setGrabXOverride,
} from '@hooks/useGrabXVariable';
import { FEATURE_FLAGS } from '@constants/common';
import { isProdEnv } from '@utils/index';

const FeatureFlagFAB = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [overrides, setOverrides] = useState<Record<string, boolean>>(getGrabXOverrides);

  if (isProdEnv()) return null;

  const applyAndReload = (updatedOverrides: Record<string, boolean>) => {
    setOverrides(updatedOverrides);
    clearGrabXCache();
    window.location.reload();
  };

  const handleToggle = (flagKey: string, enabled: boolean) => {
    setGrabXOverride(flagKey, enabled);
    applyAndReload(getGrabXOverrides());
  };

  const handleClearOverride = (flagKey: string) => {
    clearGrabXOverride(flagKey);
    applyAndReload(getGrabXOverrides());
  };

  const handleResetAll = () => {
    clearAllGrabXOverrides();
    clearGrabXCache();
    window.location.reload();
  };

  const activeCount = Object.keys(overrides).length;

  return (
    <>
      <FloatButton
        icon={<FlagFilled />}
        tooltip="Feature Flags"
        style={{ backgroundColor: '#22c55e', left: '240px', bottom: '24px' }}
        onClick={() => setIsOpen(true)}
      />

      <Drawer
        title={
          <Space>
            <FlagFilled style={{ color: '#22c55e' }} />
            <Typography.Text strong>Feature Flags</Typography.Text>
          </Space>
        }
        placement="right"
        width={360}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        closeIcon={<CloseUniversal />}
        extra={
          <Tooltip title="Reset all overrides">
            <Button size="small" danger onClick={handleResetAll}>
              Reset All
            </Button>
          </Tooltip>
        }
        footer={
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {activeCount} override{activeCount !== 1 ? 's' : ''} active · non-prod only
          </Typography.Text>
        }
      >
        {FEATURE_FLAGS.map(({ key, label, grabXUrl }, index) => {
          const hasOverride = key in overrides;
          const overrideVal = overrides[key];
          return (
            <div key={key}>
              <div className="flex items-center justify-between py-3">
                <div className="mr-3 flex min-w-0 flex-col gap-0.5">
                  <Typography.Text strong style={{ fontSize: 14 }} ellipsis>
                    {label}
                  </Typography.Text>
                  <Typography.Link
                    href={grabXUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 12, wordBreak: 'break-all', color: '#707070', textDecoration: 'underline' }}
                  >
                    {key}
                  </Typography.Link>
                  {hasOverride && (
                    <Typography.Link style={{ fontSize: 12 }} onClick={() => handleClearOverride(key)}>
                      Use GrabX default
                    </Typography.Link>
                  )}
                </div>
                <Switch
                  size="small"
                  checked={hasOverride ? overrideVal : false}
                  onChange={(checked) => handleToggle(key, checked)}
                  style={hasOverride ? undefined : { opacity: 0.4 }}
                />
              </div>
              {index < FEATURE_FLAGS.length - 1 && <Divider style={{ margin: 0 }} />}
            </div>
          );
        })}
      </Drawer>
    </>
  );
};

export default FeatureFlagFAB;
